/**
 * Adapter: ScheduleRow (backend shape, snake_case) → PositionRow (frontend
 * shape, camelCase) so the execution-schedule view can reuse the existing
 * StrategyPositionsModal for rich leg-level detail when actual Position
 * records are overlaid.
 *
 * Only rows with a real position_id produce meaningful output; synthesized
 * rows are still converted but most fields end up null — the modal renders
 * them as "—" which is the correct affordance.
 */
import type { PositionRow, OrderSide, PositionStatusValue, ExitTypeValue } from '../types';
import type { ScheduleRow } from '../types/schedule';

export function scheduleToPositionRow(row: ScheduleRow): PositionRow {
  const side: OrderSide = (row.action || row.leg.action || 'BUY') as OrderSide;

  return {
    basketId: row.basket_id,
    basketName: row.basket_name,
    // Backend marketplace flag on the schedule row; absent on legacy / non-
    // marketplace rows so the nullish-coalesce defaults to false.
    isMarketplace: row.is_marketplace ?? false,
    strategyId: row.strategy_id,
    strategyName: row.strategy_name,
    clientId: row.client_id,
    product: row.product || row.product_type || '',
    positionId: row.position_id || row.row_key,   // fallback: stable key for React list
    symbol: row.symbol,
    side,
    // `quantity` is the broker-leg contract count (e.g. 65 for 1 NIFTY lot).
    // For synthesized rows (no position yet) it's 0 — the modal's `×{qty}`
    // badge renders nothing via the qty>0 gate. NEVER fall back to leg.lots
    // here: `lots` is a lot count (1) not a contract count and renders as a
    // misleading "×1".
    qty: row.quantity,
    // Backend-derived effective lots (qty / lot_size). Null for synthesized
    // rows or when underlying lookup fails — formatQtyWithLots degrades to
    // plain qty.
    lots: row.lots ?? null,
    // entryPrice = LIMIT on the order ticket (matches PositionRecord.entry_price,
    // distinct from the fill in average_entry_price).
    entryPrice: row.entry_price ?? null,
    exitPrice: row.exit_average_price ?? row.expected_exit_price,
    // entryTime must be an ISO timestamp — the modal passes it to
    // splitTimeDate(new Date(...)). Falling back to scheduled_entry_time
    // ("09:44") produces "Invalid Date / NaN-NaN". Falling back to
    // created_at lets TRIGGER_PENDING entries render their "placed at"
    // time; the status badge disambiguates placed vs filled.
    entryTime: row.entry_filled_at || row.created_at || '',
    exitTime: row.exit_placed_at,
    exitType: (row.exit_type as ExitTypeValue) ?? null,
    ltp: row.ltp,
    pnl: row.pnl,
    // Synthesized rows (no Position record yet) leave status null. Do NOT
    // default to ENTRY_PENDING — that makes a placeholder leg look like it
    // has a live broker order and surfaces a bogus Cancel button (whose
    // action would target row_key, not a real position_id).
    status: (row.position_status as PositionStatusValue) ?? null,
    entryStatus: row.entry_status ?? '',
    entryError: row.entry_error_message,
    slStatus: row.sl_status ?? '',
    exitStatus: row.exit_status ?? undefined,
    exitAttemptCount: row.exit_attempt_count,
    exitError: row.exit_error_message,
    exitViaSlModify: row.exit_via_sl_modify,
    tag: row.position_id ? `QE_${row.position_id}` : row.row_key,

    legId: row.leg.leg_id,
    legIndex: row.leg.leg_index,
    isHedge: row.leg.is_hedge ?? false,
    selectionMethod: row.leg.selection_method || undefined,
    selectionValue: row.leg.selection_value ?? null,
    entrySource: row.entry_source,
    sequenceNo: row.sequence_no,
    parentPositionId: row.parent_position_id,

    expectedEntryPrice: row.expected_entry_price,
    averageEntryPrice: row.average_entry_price,
    entrySlippage: row.entry_slippage,
    entryAttemptCount: row.entry_attempt_count,
    entryRepriceCount: row.entry_reprice_count ?? 0,
    entryTriggerPrice: row.entry_trigger_price,
    // Phase 3 circuit-cap recovery: canonical (user-configured) entry trigger
    // and capped-state flag. Drives the "capped" chip on the Entry cell when
    // the broker clamped `entry_trigger_price` below the canonical level.
    // Legacy rows omit these (default null / false).
    entryDesiredTriggerPrice: row.entry_desired_trigger_price ?? null,
    entryCircuitCapped: row.entry_circuit_capped ?? false,
    entryFilledAt: row.entry_filled_at,

    slTriggerPrice: row.sl_trigger_price,
    // Phase 2 circuit-cap recovery — same shape as the entry side above.
    slDesiredTriggerPrice: row.sl_desired_trigger_price ?? null,
    slCircuitCapped: row.sl_circuit_capped ?? false,
    slLimitPrice: row.sl_limit_price,
    slAveragePrice: row.sl_average_price,
    slAttemptCount: row.sl_attempt_count,
    slRepriceCount: row.sl_reprice_count ?? 0,
    slError: row.sl_error_message,
    slPlacedAt: row.sl_placed_at,
    slUpdatedAt: row.sl_updated_at,
    slOrderId: row.sl_order_id,
    slConfig: row.stop_loss_config,

    exitAveragePrice: row.exit_average_price,
    expectedExitPrice: row.expected_exit_price,
    exitLimitPrice: row.exit_limit_price,
    exitPlacedAt: row.exit_placed_at,
    exitSlippage: row.exit_slippage,

    waitAndTradeConfig: row.wait_and_trade_config,
    orbConfig: row.orb_config,

    // ── Phase 1 — feature configs & state (all safe-nullable fallbacks) ───────
    targetProfitConfig:      row.target_profit_config ?? null,
    trailingSlConfig:        row.trailing_stop_loss_config ?? null,
    reEntryConfig:           row.re_entry_config ?? null,
    reExecuteConfig:         row.re_execute_config ?? null,
    moveSlToCostConfig:      row.move_sl_to_cost_config ?? null,
    reEntryCount:            row.re_entry_count ?? 0,
    tpReexecCount:           row.tp_reexec_count ?? 0,
    reEntrySpawned:          row.re_entry_spawned ?? false,
    targetProfitSpawned:     row.target_profit_spawned ?? false,
    reEntryGateArmed:        row.re_entry_gate_armed ?? false,
    tslInitialSlTrigger:     row.tsl_initial_sl_trigger ?? null,
    tslExtremePrice:         row.tsl_extreme_price ?? null,
    tslModifyCount:          row.tsl_modify_count ?? 0,
    slMovedToCost:           row.sl_moved_to_cost ?? false,
    pendingExitType:         row.pending_exit_type ?? null,
    rootExpectedEntryPrice:  row.root_expected_entry_price ?? null,
    positionExpiryType:      row.position_expiry_type ?? null,
    positionExpiryDate:      row.position_expiry_date ?? null,

    // ── UL projected fields (Phase 6 review-fix H4) ─────────────────────────
    // Copy through so position-detail modals can surface UL spot anchors.
    // Snake_case on ScheduleRow → camelCase on PositionRow; nullish-coalesce
    // keeps legacy rows / premium legs at null instead of 0 so downstream
    // renderers can distinguish "no UL anchor" from "spot is exactly zero".
    underlyingReferencePrice:     row.underlying_reference_price ?? null,
    underlyingEntryTriggerPrice:  row.underlying_entry_trigger_price ?? null,
    underlyingEntryPrice:         row.underlying_entry_price ?? null,
    underlyingExitPrice:          row.underlying_exit_price ?? null,
    underlyingSlLevel:            row.underlying_sl_level ?? null,
    underlyingTargetLevel:        row.underlying_target_level ?? null,
    ulTslInitialSlLevel:          row.ul_tsl_initial_sl_level ?? null,
    ulTslExtremeSpot:             row.ul_tsl_extreme_spot ?? null,
    ulTslModifyCount:             row.ul_tsl_modify_count ?? 0,
    tradeOnlyFirstEntry:          row.trade_only_first_entry ?? false,

    source: row.source,
    executionId: row.execution_id || undefined,
    executionDate: row.execution_date,
    closedDate: row.closed_date,

    // Today-only missed-exit signal — backend omits the field on healthy
    // positions and on past-day views, so the nullish-coalesce here is
    // enough to keep PositionRow.missedExit ABSENT for clean rows.
    missedExit: row.missed_exit,
  };
}
